import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {useIntl} from "react-intl";
import {useCallback, useEffect, useState} from "react";

import {authStore} from "../../../../store/auth.store";
import {
  getFirstDayOfWeekPreference,
  setFirstDayOfWeekPreference,
} from "../../../../store/activity.store";
import {onStartActions} from "../../../../constant/settings";
import {ActionOnStart} from "../../../../enum/activity.enum";
import { Card, CardHeader, CardTitle, CardContent } from "../../../ui/card";
import { Input } from "../../../ui/input";
import { Button } from "../../../ui/button";
import { Checkbox } from "../../../ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../ui/select";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from "../../../ui/form";

const UserForm = () => {
  const { formatMessage } = useIntl();

  const FIRST_DAY_AUTO = 'auto' as const;
  const firstDayOptions: Array<{ value: typeof FIRST_DAY_AUTO | number; label: string }> = [
    { value: FIRST_DAY_AUTO, label: formatMessage({ id: 'FIRST_DAY_AUTO' }) },
    { value: 0, label: formatMessage({ id: 'SUNDAY' }) },
    { value: 1, label: formatMessage({ id: 'MONDAY' }) },
    { value: 6, label: formatMessage({ id: 'SATURDAY' }) },
  ];

  const formSchema = z.object({
    username: z.string(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    settings: z.object({
      actionOnStart: z.nativeEnum(ActionOnStart).optional(),
      fullPath: z.boolean().optional(),
    }).optional(),
  });

  type FormValues = z.infer<typeof formSchema>;

  const editUser = authStore(state => state.editUser);
  const user = authStore(state => state.user);
  const editUserLoading = authStore(state => state.loading.editUser);

  const [firstDayOfWeek, setFirstDayOfWeek] = useState<number | null>(
    () => getFirstDayOfWeekPreference()
  );

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      username: "",
      firstName: "",
      lastName: "",
      settings: {
        actionOnStart: undefined,
        fullPath: false,
      },
    },
  });

  useEffect(() => {
    if (user) {
      form.reset({
        username: user.username || "",
        firstName: user.firstName || "",
        lastName: user.lastName || "",
        settings: {
          actionOnStart: user.settings?.actionOnStart,
          fullPath: user.settings?.fullPath || false,
        },
      });
    }
  }, [user, form]);

  const onSubmit = useCallback((values: FormValues) => {
    setFirstDayOfWeekPreference(firstDayOfWeek);
    editUser(values as any);
  }, [editUser, firstDayOfWeek]);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{formatMessage({ id: 'SETTINGS' })}</CardTitle>
      </CardHeader>
      <CardContent>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="username"
              render={({ field }) => (
                <FormItem className="grid grid-cols-4 items-center gap-4">
                  <FormLabel className="text-right">{formatMessage({ id: 'USER_NAME' })}</FormLabel>
                  <FormControl>
                    <Input disabled className="col-span-3" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem className="grid grid-cols-4 items-center gap-4">
                  <FormLabel className="text-right">{formatMessage({ id: 'FIRST_NAME' })}</FormLabel>
                  <FormControl>
                    <Input className="col-span-3" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem className="grid grid-cols-4 items-center gap-4">
                  <FormLabel className="text-right">{formatMessage({ id: 'LAST_NAME' })}</FormLabel>
                  <FormControl>
                    <Input className="col-span-3" {...field} />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="settings.actionOnStart"
              render={({ field }) => (
                <FormItem className="grid grid-cols-4 items-center gap-4">
                  <FormLabel className="text-right">{formatMessage({ id: 'ON_START_ACTION' })}</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger className="col-span-3">
                        <SelectValue />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {onStartActions.map(action => (
                        <SelectItem key={action} value={action}>
                          {formatMessage({ id: action })}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="settings.fullPath"
              render={({ field }) => (
                <FormItem className="grid grid-cols-4 items-center gap-4">
                  <FormLabel className="text-right">{formatMessage({ id: 'FULL_PATH' })}</FormLabel>
                  <FormControl>
                    <Checkbox
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormItem className="grid grid-cols-4 items-center gap-4">
              <FormLabel className="text-right">{formatMessage({ id: 'FIRST_DAY_OF_WEEK' })}</FormLabel>
              <Select
                onValueChange={(v) => setFirstDayOfWeek(v === FIRST_DAY_AUTO ? null : Number(v))}
                value={firstDayOfWeek === null ? FIRST_DAY_AUTO : String(firstDayOfWeek)}
              >
                <SelectTrigger className="col-span-3">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {firstDayOptions.map(opt => (
                    <SelectItem key={String(opt.value)} value={String(opt.value)}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormItem>

            <Button
              loading={editUserLoading}
              type="submit"
            >
              {formatMessage({ id: 'SAVE' })}
            </Button>
          </form>
        </Form>
      </CardContent>
    </Card>
  );
};

export default UserForm;
